import { SECTIONS, type AdminArea, type AdminUser, type Me, type Role, type Section } from '@ft/shared';
import { useCallback, useState } from 'react';
import { useI18n, type T } from '../../i18n';
import { api } from '../../lib/http';
import { getAreaId } from '../../lib/area';
import { Loading, Modal, useLoad, useToast } from '../ui';

const ROLES: Role[] = ['pm', 'admin', 'finance', 'reader'];
/** Auswahl im Formular: die drei Rollen plus „Delivery Head“ (= Lesend mit Zugriff auf alle Bereiche) */
type RoleChoice = Role | 'deliveryHead';
const roleLabel = (u: { role: Role; deliveryHead: boolean }, t: T) => {
  if (!u.deliveryHead) return t(`role.${u.role}`);
  return u.role === 'reader' ? t('role.deliveryHead') : `${t(`role.${u.role}`)} + ${t('role.deliveryHead')}`;
};

export function UsersAdmin({ me }: { me: Me }) {
  const { t } = useI18n();
  const { data, error, reload } = useLoad<AdminUser[]>('/api/admin/users');
  const { data: areas } = useLoad<AdminArea[]>('/api/admin/areas');
  const areaNames = (ids: string[]) => (areas ?? []).filter((a) => ids.includes(a.id)).map((a) => a.name).join(', ');
  const [form, setForm] = useState<{ user: AdminUser | null } | null>(null);
  const [toast, say] = useToast();

  return (
    <>
      <div className="row" style={{ alignItems: 'flex-start' }}>
        <p className="muted lead" style={{ flex: 1 }}>{t('users.lead')}</p>
        <button className="btn primary" onClick={() => setForm({ user: null })} disabled={!areas}>{t('users.add')}</button>
      </div>
      {!data ? <Loading error={error} /> : (
        <div className="box tablewrap">
          <table>
            <thead><tr><th>{t('users.col.name')}</th><th>{t('users.col.email')}</th><th>{t('users.col.role')}</th><th>{t('users.col.areas')}</th><th>{t('users.col.projects')}</th><th>{t('users.col.state')}</th></tr></thead>
            <tbody>
              {data.map((u) => (
                <tr key={u.id} className={`click${u.active ? '' : ' inactive'}`} tabIndex={0} onClick={() => setForm({ user: u })} onKeyDown={(e) => { if (e.key === 'Enter') setForm({ user: u }); }}>
                  <td><span className="pmc">{u.initials}</span> {u.name} {u.id === me.id && <span className="muted">{t('users.you')}</span>}</td>
                  <td className="mono">{u.email}</td>
                  <td>{roleLabel(u, t)}{u.logAccess && <> · {t('uf.logAccessShort')}</>}{u.sections && <div className="muted small">{u.sections.map((x) => t(`section.${x}`)).join(', ')}</div>}</td>
                  <td>{u.deliveryHead && <span className="pill info">{t('users.allAreas')}</span>} {u.areaIds.length ? areaNames(u.areaIds) : !u.deliveryHead && <span className="pill crit">{t('users.noArea')}</span>}</td>
                  <td className="muted">{[u.pmOf ? t('users.pmOf', { n: u.pmOf }) : null, u.deputyOf ? t('users.deputyOf', { n: u.deputyOf }) : null].filter(Boolean).join(' · ') || '–'}</td>
                  <td>{u.active ? <span className="pill pos">{t('users.active')}</span> : <span className="pill neu">{t('users.inactive')}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="muted small" style={{ margin: 0 }}>{t('users.roleHelp')}</p>
      {form && areas && <UserForm user={form.user} me={me} areas={areas} onClose={() => setForm(null)} onDone={(msg) => { setForm(null); reload(); say(msg); }} />}
      {toast}
    </>
  );
}

function UserForm({ user, me, areas, onClose, onDone }: { user: AdminUser | null; me: Me; areas: AdminArea[]; onClose: () => void; onDone: (msg: string) => void }) {
  const { t } = useI18n();
  const [name, setName] = useState(user?.name ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [choice, setChoice] = useState<RoleChoice>(user?.deliveryHead && user.role === 'reader' ? 'deliveryHead' : user?.role ?? 'pm');
  const [dhAddon, setDhAddon] = useState(!!user?.deliveryHead && user.role !== 'reader');
  // Häkchen „Protokoll“ (nur Admins): vergeben darf es nur, wer es selbst hat
  const [logAccess, setLogAccess] = useState(!!user?.logAccess);
  const role: Role = choice === 'deliveryHead' ? 'reader' : choice;
  const deliveryHead = choice === 'deliveryHead' || ((choice === 'admin' || choice === 'pm') && dhAddon);
  // Neue Personen standardmäßig im aktuell gewählten Bereich
  const [areaIds, setAreaIds] = useState<string[]>(user?.areaIds ?? (me.areas[0] ? [me.areas.find((a) => a.id === getAreaId())?.id ?? me.areas[0].id] : []));
  // Rolle „lesend“: freigeschaltete Seiten (Standard: alle)
  const [sections, setSections] = useState<Section[]>(user?.sections ?? [...SECTIONS]);
  const limitable = role === 'reader' && !deliveryHead;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState(false);
  const close = useCallback(() => { if (!busy) onClose(); }, [busy, onClose]);
  const self = user?.id === me.id;

  async function go(fn: () => Promise<unknown>, msg: string) {
    setBusy(true);
    setError(null);
    try { await fn(); onDone(msg); } catch (e) { setError((e as Error).message); setBusy(false); setConfirm(false); }
  }

  function save() {
    if (name.trim().length < 2) return setError(t('uf.err.name'));
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return setError(t('uf.err.email'));
    if (limitable && !sections.length) return setError(t('uf.err.sections'));
    const body = { version: user?.version, name: name.trim(), email: email.trim(), role, areaIds, deliveryHead,
      sections: limitable && sections.length < SECTIONS.length ? sections : null, ...(me.logAccess && role === 'admin' ? { logAccess } : {}) };
    void go(() => (user ? api('PUT', `/api/admin/users/${user.id}`, body) : api('POST', '/api/admin/users', body)), t('uf.saved'));
  }

  const inactive = user && !user.active;
  return (
    <Modal title={user ? t('uf.edit', { n: user.name }) : t('uf.new')} crumb={t('admin.title')} onClose={close} width={560}
      footer={<>
        {error && <p className="err" role="alert">{error}</p>}
        {user && user.active && !self && (!confirm
          ? <button className="btn danger" onClick={() => setConfirm(true)} disabled={busy}>{t('users.deactivate')}</button>
          : <button className="btn danger" onClick={() => go(() => api('DELETE', `/api/admin/users/${user.id}?version=${user.version}`), t('uf.deactivated'))} disabled={busy}>{t('users.deactivateConfirm')}</button>)}
        {inactive && <button className="btn" onClick={() => go(() => api('POST', `/api/admin/users/${user.id}/restore`), t('uf.restored'))} disabled={busy}>{t('users.restore')}</button>}
        <button className="btn" onClick={close} disabled={busy}>{t('common.cancel')}</button>
        {!inactive && <button className="btn primary" onClick={save} disabled={busy}>{busy ? t('common.saving') : t('common.save')}</button>}
      </>}>
      <form className="form" onSubmit={(e) => { e.preventDefault(); save(); }}>
        <div className="fld full">
          <label htmlFor="uf-n">{t('uf.name')} <span className="req">*</span></label>
          <input id="uf-n" value={name} onChange={(e) => setName(e.target.value)} placeholder={t('uf.namePh')} autoComplete="off" disabled={!!inactive} />
        </div>
        <div className="fld full">
          <label htmlFor="uf-e">{t('uf.email')} <span className="req">*</span></label>
          <input id="uf-e" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t('uf.emailPh')} autoComplete="off" disabled={!!inactive} />
        </div>
        <div className="fld full">
          <label htmlFor="uf-r">{t('uf.role')}</label>
          <select id="uf-r" value={choice} onChange={(e) => setChoice(e.target.value as RoleChoice)} disabled={!!inactive || self}>
            {ROLES.map((r) => <option key={r} value={r}>{t(`role.${r}`)}</option>)}
            <option value="deliveryHead">{t('role.deliveryHead')}</option>
          </select>
          <span className="help">{t('users.roleHelp')}</span>
          {(choice === 'admin' || choice === 'pm') && (
            <label className="checkline">
              <input type="checkbox" checked={dhAddon} onChange={(e) => setDhAddon(e.target.checked)} disabled={!!inactive} />
              {t('uf.dhAddon')}
            </label>
          )}
          {deliveryHead && <span className="help">{t('uf.dhHelp')}</span>}
          {choice === 'finance' && <span className="help">{t('uf.financeHelp')}</span>}
          {choice === 'admin' && (me.logAccess || logAccess) && (
            <label className="checkline">
              <input type="checkbox" checked={logAccess} onChange={(e) => setLogAccess(e.target.checked)} disabled={!!inactive || !me.logAccess || self} />
              {t('uf.logAccess')}
            </label>
          )}
          {choice === 'admin' && (me.logAccess || logAccess) && <span className="help">{t('uf.logAccessHelp')}</span>}
        </div>
        <fieldset className="fld full" style={{ border: 0, padding: 0, margin: 0 }}>
          <legend className="flabel" style={{ marginBottom: 6 }}>{t('uf.areas')}</legend>
          <div className="checklist">
            {areas.map((a) => (
              <label key={a.id}>
                <input type="checkbox" checked={areaIds.includes(a.id)} disabled={!!inactive}
                  onChange={(e) => setAreaIds((ids) => (e.target.checked ? [...ids, a.id] : ids.filter((x) => x !== a.id)))} />
                {a.name}
              </label>
            ))}
          </div>
          <span className="help">{t('uf.areasHelp')}</span>
        </fieldset>
        {limitable && (
          <fieldset className="fld full" style={{ border: 0, padding: 0, margin: 0 }}>
            <legend className="flabel" style={{ marginBottom: 6 }}>{t('uf.sections')}</legend>
            <div className="checklist">
              {SECTIONS.map((sec) => (
                <label key={sec}>
                  <input type="checkbox" checked={sections.includes(sec)} disabled={!!inactive}
                    onChange={(e) => setSections((l) => (e.target.checked ? SECTIONS.filter((x) => x === sec || l.includes(x)) : l.filter((x) => x !== sec)))} />
                  {t(`section.${sec}`)}
                </label>
              ))}
            </div>
            <span className="help">{t('uf.sectionsHelp')}</span>
          </fieldset>
        )}
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}
